// src/components/reports/SupervisorReport/index.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { salesReportService } from '../../../../services/salesReportService';
import { useAuth } from 'react-oidc-context';
import { useReportContext } from '../../../../contexts/ReportContext';
import { SupervisorChart } from './SupervisorChart';
import ViewModeTabs from './ViewModeTabs';
import SupervisorTable from './SupervisorTable';
import SupervisorDetailedReport from './SupervisorDetailedReport';

// Import test data generation function (adjust path as needed)
import { generateMockSupervisorData } from '../../../../utils/testData';

// Development mode flag - whether to use test data
const USE_TEST_DATA = false; // true to use test data, false to call actual API

interface SupervisorReportProps {
  startMonth: string;
  endMonth: string;
}

export const SupervisorReport: React.FC<SupervisorReportProps> = ({
  startMonth,
  endMonth
}) => {
  const auth = useAuth();
  const [viewMode, setViewMode] = useState<'report' | 'chart' | 'table'>('report');

  // Get required functions from ReportContext
  const {
    isCacheValid,
    getCachedData,
    cacheReportData,
    markReportAsDisplayed
  } = useReportContext();

  // Check cache
  const cacheKey = 'supervisor';
  const shouldFetch = !isCacheValid(cacheKey);
  const cachedData = getCachedData(cacheKey);

  // TEST_MODE data
  const testData = USE_TEST_DATA ? generateMockSupervisorData() : null;

  // Use React Query for data fetching
  const { data, isLoading, error } = useQuery({
    queryKey: ['supervisorReport', { startMonth, endMonth }],
    queryFn: async () => {
      // Return mock data if in test mode
      if (USE_TEST_DATA) {
        // Simulate API request delay
        await new Promise(resolve => setTimeout(resolve, 800));
        return testData;
      }
      
      // Normal API call
      const token = auth.user?.access_token;
      return await salesReportService.getSupervisorReport(
        startMonth,
        endMonth,
        token);
    },
    enabled: !USE_TEST_DATA && (shouldFetch || !cachedData), // Skip cache check in test mode
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
  });

  // Handle successful data fetch
  useEffect(() => {
    if (data) {
      // Save data to context cache
      cacheReportData(cacheKey, data);
      // Mark report as displayed
      markReportAsDisplayed();
    }
  }, [data, cacheReportData, cacheKey, markReportAsDisplayed]);

  // Handle error
  useEffect(() => {
    if (error) {
      console.error('Failed to retrieve supervisor report:', error);
    }
  }, [error]);

  // Use test data in test mode, otherwise use cache or newly fetched data
  const reportData = USE_TEST_DATA 
    ? testData 
    : ((shouldFetch || !cachedData) ? data : cachedData);

    console.log("reportData", reportData);

  // View mode change handler
  const handleViewModeChange = (event: React.SyntheticEvent, newValue: 'report' | 'chart' | 'table') => {
    setViewMode(newValue);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        データの取得中にエラーが発生しました
      </Alert>
    );
  }

  if (!reportData || reportData.length === 0) {
    return (
      <Alert severity="info">
        該当するデータがありません。条件を変更して再度お試しください。
      </Alert>
    );
  }

  // グラフのタイトル（共通）
  const chartTitle = `担当者別売上・利益 (${startMonth} 〜 ${endMonth})`;

  return (
    <Box>
      <ViewModeTabs 
        viewMode={viewMode} 
        onViewModeChange={handleViewModeChange} 
      />

      {viewMode === 'report' && (
        <SupervisorDetailedReport
          data={reportData}
          startMonth={startMonth}
          endMonth={endMonth}
          showPrintButtons={true}
          chartTitle={chartTitle}  // 共通のチャートタイトルを渡す
        />
      )}

      {viewMode === 'chart' && (
        <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            ※参考情報：グラフ表示
          </Typography>
          <SupervisorChart
            data={reportData}
            title={chartTitle}
            fullSize={true}  // グラフビューでは大きなサイズで表示する
          />
        </Box>
      )}

      {viewMode === 'table' && (
        <SupervisorTable data={reportData} />
      )}
    </Box>
  );
};